import Vue from 'vue'
import { Injector } from '@/config/dependency-injection'
import { TranslateService } from '@/shared/services/translate-service'
import { ToastAction, ToastObject, ToastOptions, ToastType } from 'vue-toasted'
import { NumberMap } from '@/shared/utils/map'
import { loggerFor } from '@/shared/utils/logger'
import { transformError } from '@/shared/utils/transform-error'

type ErrorStatus = string | ((error: Response) => string) | NumberMap<any>

export class NotificationService {
  private logger = loggerFor('NotificationService')
  private translateService = Injector.get(TranslateService)

  constructor(private vue: Vue) {}

  public error(error: Response, status: ErrorStatus = {}): void {
    this.logger.error(error)

    this.clearToasts()

    const message = transformError(error, status)

    this.showMessage(message, 'error', {
      closeOnSwipe: false,
      duration: undefined,
      action: {
        onClick: (e, toastObject) => {
          toastObject.goAway(0)
        },
        text: this.translateService.translate('common.close'),
      },
    })
  }

  public errorHandler(status: ErrorStatus = {}): (error: Response) => void {
    return (error: Response) => this.error(error, status)
  }

  public isLoading(loader = 'global-loader'): boolean {
    return this.vue.$wait.is(loader)
  }

  public showDefault(message: string) {
    this.showTranslatedMessage(message, 'default')
  }

  public showError(message: string) {
    this.showTranslatedMessage(message, 'error', {
      action: {
        onClick(e, toastObject) {
          toastObject.goAway(0)
        },
        text: this.translateService.translate('common.close'),
      },
      duration: 0,
    })
  }

  public showInfo(message: string) {
    this.showTranslatedMessage(message, 'info')
  }

  /**
   * Show a message to the user
   *
   * @param message The message to show (this won't be translated)
   * @param type Type of the message
   * @param options Additional options that are passed to the underlying toast implementation.
   */
  public showMessage(
    message: string,
    type: ToastType | string = 'default',
    options: ToastOptions = {}
  ) {
    const defaultOptions: ToastOptions = {
      duration: 2500,
    }

    options = options ?? {}

    this.vue.$toasted.show(message, {
      ...defaultOptions,
      ...options,
      type,
    })
  }

  public showSuccess(message: string) {
    this.showTranslatedMessage(message, 'success')
  }

  public showTranslatedMessage(
    translationKey: string,
    type: ToastType | string,
    options?: ToastOptions
  ) {
    this.showMessage(this.translateService.translate(translationKey), type, options)
  }

  public startLoading(loader = 'global-loader'): void {
    this.vue.$wait.start(loader)
  }

  public success(
    text = 'common.success',
    icon?: string,
    action?: ToastAction | ToastAction[]
  ): void {
    text = this.translateService.translate(text)

    if (action === undefined) {
      action = {
        text: this.translateService.translate('common.close'),
        onClick(e, toast: ToastObject): void {
          toast.goAway(0)
        },
      }
    }

    const options: Overwrite<
      ToastOptions,
      {
        icon?: ((ToastIcon: HTMLElement) => HTMLElement) | string | { name: string; after: boolean }
      }
    > = {
      action,
      containerClass: 'mt-n2',
      duration: 3500,
      iconPack: 'callback',
      position: 'top-center',
      type: 'default',
    }

    if (icon !== undefined) {
      options.icon = el => {
        el.innerHTML = `<i class="${icon} mr-2">`

        return el
      }
    }

    this.vue.$toasted.show(text, options)
  }

  public successHandler(
    text = 'common.success',
    action?: ToastAction | ToastAction[],
    icon?: string
  ): () => void {
    return () => this.success(text, icon, action)
  }

  private clearToasts(): void {
    this.vue.$toasted.clear()
  }
}

// Find the difference
type Diff<T extends string | number | symbol, U extends string | number | symbol> = ({
  [P in T]: P
} &
  { [P in U]: never } & { [x: string]: never })[T]

// Overwrite any differences between T and U with U taking precedence
type Overwrite<T, U> = Pick<T, Diff<keyof T, keyof U>> & U
